import * as Commons from "oc-commons/Commons";
import { Interval2 } from "./Interval2";
import { v3 } from "oc-threejs/short3";

import _ from "lodash";
import { Box3, Color, Euler, Quaternion, Vector2, Vector3, Vector4 } from "three";

export class Wkfg2TypeConverter {
  parsers: Record<string, Function>;
  unmarshallers: Record<string, Function>;
  formatters: Record<string, Function>;

  /** */
  constructor() {
    this.parsers = Commons.getPrefixedMethods(this, "parse_");
    this.unmarshallers = Commons.getPrefixedMethods(this, "unmarshal_");
    this.formatters = Commons.getPrefixedMethods(this, "format_");
  }

  /**
   * Wandelt eine Rückgabe der WKFG2-Backend-API (Objekt mit Tabellennamen als Keys, Werte sind Arrays von Rows)
   * in das WKFG2 - Client - Format um, wobei die Objekte in-place verändert werden.
   */
  async parse(
    metadata: any,
    maybeSleepAtTable: ((table: any) => Promise<void>) | null = null,
  ): Promise<any> {
    if (APP.metamodel == null) {
      throw new Error("metamodel not loaded");
    }
    for (const tableModel of APP.metamodel.tables) {
      const table = metadata[tableModel.name];
      if (table != null) {
        if (maybeSleepAtTable) {
          await maybeSleepAtTable(table);
        }
        this.parseTable(tableModel, table);
      }
    }
    return metadata;
  }

  /**
   * Wandelt spezifisch eine einzelne Tabelle (Array von Rows)
   * in das WKFG2 - Client - Format um, wobei die Objekte in-place verändert werden.
   */
  parseTable(tableModel: any, table: any[]): void {
    tableModel.columns.forEach((columnModel: any) => {
      const isOne2Many = columnModel.multiselect || columnModel.variantKey;
      let typeParser = this.parsers[isOne2Many ? "List" : columnModel.jsType || columnModel.type];
      if (typeParser != null) {
        let typeParserOnlyStringInput = (x: any) =>
          _.isString(x) ? typeParser(x, columnModel, tableModel) : x;
        const jsDefault = Commons.isEmpty(columnModel.jsDefault)
          ? null
          : typeParserOnlyStringInput(columnModel.jsDefault);
        table.forEach((row) => {
          const rowValue = row[columnModel.name];
          row[columnModel.name] = Commons.isEmpty(rowValue)
            ? jsDefault
            : typeParserOnlyStringInput(rowValue);
        });
      }
      const unmarshaller = this.unmarshallers[columnModel.jsType || columnModel.type];
      if (unmarshaller != null) {
        table.forEach((row) => {
          const rowValue = row[columnModel.name];
          row[columnModel.name] =
            rowValue == null ? null : unmarshaller(rowValue, columnModel, tableModel);
        });
      }
    });
  }

  /**
   * Wandelt Settings in das WKFG2 - Client - Format um, wobei die Objekte in-place verändert werden.
   */
  parseSettings(settings: any, settingsMeta: any[]): void {
    settingsMeta.forEach((meta) => {
      if (meta.serverOnly) return; // niemals serverseitige Keys an den Client weitergeben
      const value = this.parseSingleSetting(meta.Value, meta);
      _.set(settings, meta.Key, value);
    });
  }

  /** */
  parseSingleSetting(value: any, meta: any): any {
    if (!_.isString(value)) {
      return value;
    }
    let typeParser = this.parsers[meta.Type];
    return typeParser == null ? value : typeParser(value, meta);
  }

  /**
   * Erstellt eine Kopie eines Datensatz, wandelt diese in das WKFG2 - Backend - Format um und gibt sie zurück.
   * @param {string} tableName
   * @param {?Object} row
   * @returns {?Object}
   */
  format(tableName: string, row: any): any {
    if (row == null) {
      return null;
    }
    if (APP.metamodel == null) {
      throw new Error("metamodel not loaded");
    }
    const result = Commons.cloneDeep(row);
    const tableModel = APP.metamodel.tables.find((table: any) => table.name == tableName);
    tableModel.columns.forEach((columnModel: any) => {
      const typeName = columnModel.jsType || columnModel.type;
      let typeFormatter = this.formatters[typeName];
      if (typeFormatter != null) {
        const rowValue = result[columnModel.name];
        if (rowValue == null) {
          let typeParser = this.parsers[typeName] || ((x: any) => x);
          result[columnModel.name] = Commons.isEmpty(columnModel.jsDefault)
            ? null
            : typeFormatter(typeParser(columnModel.jsDefault), columnModel);
        } else {
          result[columnModel.name] = typeFormatter(rowValue, columnModel);
        }
      }
    });
    return result;
  }

  /**
   * Erstellt eine leere mit default Werten gefüllte Instance einer Zeile basierend auf den TableDaten
   * @param {string} tableName
   * @returns {?Object}
   */
  createInstance(tableName: string): any {
    if (APP.metamodel == null) {
      throw new Error("metamodel not loaded");
    }
    const tableModel = APP.metamodel.tables.find((table: any) => table.name == tableName);
    if (tableModel != null) {
      const result: any = {};
      tableModel.columns.forEach((columnModel: any) => {
        const typeParser = this.parsers[columnModel.jsType || columnModel.type];
        let jsDefault = Commons.isEmpty(columnModel.jsDefault) ? null : columnModel.jsDefault;
        if (typeParser != null) {
          jsDefault = Commons.isEmpty(columnModel.jsDefault)
            ? null
            : typeParser(columnModel.jsDefault);
        }
        result[columnModel.name] = jsDefault;
      });
      return result;
    } else {
      return null;
    }
  }

  parseFloat(s: any): number {
    if (s == null) {
      return 0;
    }
    // das hier ist die JS-Standardfunktion:
    return parseFloat(s);
  }

  ///////////////////////////////////////////////////////////////////
  // interne konkrete Type-Parser
  // Der reingereichte Wert ist immer ein String und niemals null oder empty,
  // das nimmt schon die Methode transformTypes vorweg,
  // und auch jsDefault
  ///////////////////////////////////////////////////////////////////

  /**
   * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
   */
  parse_Float(v: string): number {
    return this.parseFloat(v);
  }

  /**
   * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
   */
  parse_Int(v: string): number {
    return v == null ? 0 : parseInt(v, 10);
  }

  /**
   * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
   */
  parse_Bool(v: string | boolean): boolean {
    return v === true || v === "true";
  }

  /**
   * eine beliebig lange Liste von Zahlen
   */
  parse_Vector(v: string): number[] {
    return v.split(/\s*,\s*/).map((x) => parseFloat(x));
  }

  /** */
  parse_Vector2(v: string): Vector2 {
    const p = v.split(/\s*,\s*/);
    const o = new Vector2();
    o.x = this.parseFloat(p[0]);
    o.y = this.parseFloat(p[1]);
    return o;
  }

  /** */
  parse_Vector3(v: string): Vector3 {
    const p = v.split(/\s*,\s*/);
    const o = new Vector3();
    o.x = this.parseFloat(p[0]);
    o.y = this.parseFloat(p[1]);
    o.z = this.parseFloat(p[2]);
    return o;
  }

  /** */
  parse_Vector4(v: string): Vector4 {
    const p = v.split(/\s*,\s*/);
    const o = new Vector4();
    o.x = this.parseFloat(p[0]);
    o.y = this.parseFloat(p[1]);
    o.z = this.parseFloat(p[2]);
    o.w = this.parseFloat(p[3]);
    return o;
  }

  /** */
  parse_Box3(v: string): Box3 {
    const p = v.split(/\s*,\s*/);
    const a = new Vector3();
    a.x = this.parseFloat(p[0]);
    a.y = this.parseFloat(p[1]);
    a.z = this.parseFloat(p[2]);
    const b = new Vector3();
    b.x = this.parseFloat(p[3]);
    b.y = this.parseFloat(p[4]);
    b.z = this.parseFloat(p[5]);
    return new Box3(a, b);
  }

  /** */
  parse_Map(v: string): Record<string, string> {
    const map: Record<string, string> = {};
    if (v.length > 0) {
      let pairs: string[] | undefined;
      try {
        //https://caniuse.com/js-regexp-lookbehind
        const allowEscapedColons = new RegExp("\\s*(?<!\\\\),\\s*", "g");
        pairs = v.split(allowEscapedColons);
      } catch (e) {
        //https://caniuse.com/?search=look%20ah
        const reverse = (st: string) => st.split("").reverse().join("");
        const allowEscapedColons = new RegExp("\\s*,(?!\\\\)\\s*", "g");
        pairs = reverse(v)
          .split(allowEscapedColons)
          ?.map((x) => reverse(x));
      }
      pairs
        ?.map((p) => p.replace(/\\,/g, ",").split(/\s*=\s*/))
        .forEach(([key, value]) => {
          if (key && value) {
            map[key] = value;
          }
        });
    }
    return map;
  }

  /** */
  parse_xMap(v: string): Record<string, number> {
    const map: Record<string, number> = {};
    v.split(/\s*,\s*/)
      .map((p) => p.split(/\s*x\s*/))
      .forEach(([key, value]) => (map[value] = this.parseFloat(key)));
    return map;
  }

  /** */
  parse_Rotation(v: string): Quaternion {
    const r = this.parse_Vector3(v);
    r.multiplyScalar(Math.PI / 180);
    return new Quaternion().setFromEuler(new Euler(r.x, r.y, r.z));
  }

  /** */
  parse_List(v: string): string[] {
    return v.split(/\s*,\s*/).filter((x) => x.length);
  }

  /** */
  parse_Color(v: string): Color {
    return new Color(v.toLowerCase());
  }

  /** */
  parse_NumberOrInterval(v: string): any {
    return Interval2.parse(v);
  }

  /** */
  parse_Image(v: any, columnMeta: any): any {
    return this._getContentUrl(v, columnMeta);
  }

  /** */
  parse_Model3D(v: any, columnMeta: any): any {
    return this._getContentUrl(v, columnMeta);
  }

  _getContentUrl(v: any, columnMeta: any): any {
    if (APP.metamodel.version >= 2) {
      return APP.getContentUrl(columnMeta, v);
    }
    return v;
  }

  ///////////////////////////////////////////////////////////////////
  // interne konkrete Type-Formatter
  // Der reingereichte Wert ist niemals null.
  ///////////////////////////////////////////////////////////////////

  /**
   * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
   */
  format_Float(v: number): string {
    return v.toString();
  }

  /**
   * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
   */
  format_Int(v: number): string {
    return v.toString();
  }

  /**
   * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
   */
  format_Bool(v: boolean): string {
    return v.toString();
  }

  /**
   * eine beliebig lange Liste von Zahlen
   */
  format_Vector(v: number[]): string {
    return v.join(",");
  }

  /** */
  format_Vector2(v: Vector2): string {
    return v.toArray().join(",");
  }

  /** */
  format_Vector3(v: Vector3): string {
    return v.toArray().join(",");
  }

  /** */
  format_Vector4(v: Vector4): string {
    return v.toArray().join(",");
  }

  /** */
  format_Box3(v: Box3): string {
    return this.format_Vector3(v.min) + "," + this.format_Vector3(v.max);
  }

  /** */
  format_Map(v: any): string {
    return _.toPairs(v)
      .map(([k, v]) => k + "=" + v)
      .join(",");
  }

  /** */
  format_xMap(v: any): string {
    return _.toPairs(v)
      .map(([k, v]) => v + "x" + k)
      .join(",");
  }

  /** */
  format_Rotation(v: Quaternion): any {
    // Note: This logic returns a string via format_Vector3 implicitly/recursively?
    // original code calls this.format_Vector3(...)
    return this.format_Vector3(
      v3()
        .setFromEuler(new Euler().setFromQuaternion(v, "XYZ"))
        .multiplyScalar((180 / Math.PI) * 1000000)
        .round()
        .multiplyScalar(1 / 1000000),
    );
  }

  /** */
  format_ModelData(v: any): any {
    const json = Commons.cloneDeep(v);
    const box = (json.boundingBoxUntransformed = json?.boundingBoxUntransformed?.toJSON?.());
    if (box?.isEmpty?.()) {
      box.set(v3(0, 0, 0), v3(0, 0, 0));
    }
    return json;
  }

  format_ConnectorList(v: any[]): any[] {
    const json = Commons.cloneDeep(v);
    json.forEach((config: any) => {
      config.position_absolut = this.format_Vector3(config.position_absolut);
      config.position_relativ = this.format_Vector3(config.position_relativ);
      config.rotation = this.format_Rotation(config.rotation);
    });
    return json;
  }

  /** */
  format_List(v: any[]): string {
    return v.join(",");
  }

  /** */
  format_Color(v: any): string {
    if (v.getHexString) return "#" + v.getHexString();
    else if (typeof v == "number") return "#" + v.toString(16);
    else if (v.startsWith("#")) return v;
    else return "#" + v;
  }

  /** */
  format_NumberOrInterval(v: any): string {
    return "" + v;
  }

  /** */
  format_Image(v: any, columnMeta: any): any {
    return this._formatContentUrl(v, columnMeta);
  }

  /** */
  format_Model3D(v: any, columnMeta: any): any {
    return this._formatContentUrl(v, columnMeta);
  }

  _formatContentUrl(v: any, columnMeta: any): any {
    if (APP.metamodel.version >= 2 && APP.formatContentValue) {
      return APP.formatContentValue(columnMeta, v);
    }
    return v;
  }

  ///////////////////////////////////////////////////////////////////
  // interne konkrete Type-Unmarshaller
  // Der reingereichte Wert ist niemals null.
  // Im Gegensatz zum Parsen bedeutet Unmarshal, dass auch nicht-String-
  // Werte verarbeitet werden können, also z.B. JSON
  ///////////////////////////////////////////////////////////////////

  /** */
  unmarshal_ModelData(v: any): any {
    if (_.isString(v)) {
      v = JSON.parse(v);
    }
    const json = Commons.cloneDeep(v);
    const unmarshalBox3 = (box: any) => (box == null ? null : new Box3().fromJSON(box));
    json.boundingBoxUntransformed = unmarshalBox3(json.boundingBoxUntransformed);
    const isArray3 = (x: any) => Array.isArray(x) && x.length == 3;
    if (isArray3(json.Rotation)) {
      json.Rotation = new Quaternion().setFromEuler(new Euler().fromArray(json.Rotation));
    }
    if (isArray3(json.GridPosition)) {
      json.GridPosition = v3().fromArray(json.GridPosition);
    }
    if (isArray3(json.Position)) {
      json.Position = v3().fromArray(json.Position);
    }
    if (isArray3(json.Scale)) {
      json.Scale = v3().fromArray(json.Scale);
    }
    return json;
  }

  unmarshal_ConnectorList(v: any): any[] {
    if (Commons.isEmpty(v)) {
      return [];
    }
    if (_.isString(v)) {
      v = JSON.parse(v);
    }
    const json = Commons.cloneDeep(v);
    json.forEach((config: any) => {
      config.position_absolut = this.parse_Vector3(config.position_absolut);
      config.position_relativ = this.parse_Vector3(config.position_relativ);
      config.rotation = this.parse_Rotation(config.rotation);
    });
    return json;
  }
}
